import ProfileHero from '../blocks/ProfileHero/ProfileHero';
import SectionTitleBlock from '../blocks/SectionTitle/SectionTitle';
import TextBlock from '../blocks/TextBlock/TextBlock';
import Header from '../blocks/Header/Header';
import CTAButton from '../blocks/CTAButton/CTAButton';
import PortfolioItem from '../blocks/PortfolioItem/PortfolioItem';
import Timeline from '../blocks/Timeline/Timeline';
import CertificateGrid from '../blocks/CertificateGrid/CertificateGrid';
import FeatureCards from '../blocks/FeatureCards/FeatureCards';
import TechStack from '../blocks/TechStack/TechStack';
import ContactLinks from '../blocks/ContactLinks/ContactLinks';
import ImageBlock from '../blocks/ImageBlock/ImageBlock';
import Carousel from '../blocks/Carousel/Carousel';
import Separator from '../blocks/Separator/Separator';
import PomodoroTimerBlock from '../blocks/PomodoroTimer/PomodoroTimer';
import EditModeToggleBlock from '../blocks/EditModeToggle/EditModeToggle';
import { demoRegistry } from './demoRegistry';

const blockRegistry = {
  ProfileHero: {
    component: ProfileHero,
    label: 'Profile Hero',
    category: 'content',
    icon: 'FaHome',
    defaultProps: {
      profileInitials: 'ABC',
      profileImage: null,
      nameKey: '',
      titleKey: '',
      bioKey: '',
      showDivider: true,
      ctaKey: '',
      ctaLink: '',
    },
    defaultStyle: { paddingTop: '80px', paddingBottom: '80px' },
    schema: [
      { key: 'nameKey', target: 'props', type: 'i18nText', label: 'Name' },
      { key: 'titleKey', target: 'props', type: 'i18nText', label: 'Job Title' },
      { key: 'bioKey', target: 'props', type: 'i18nText', label: 'Bio', multiline: true },
      { key: 'profileImage', target: 'props', type: 'image', label: 'Profile Photo' },
      { key: 'profileInitials', target: 'props', type: 'text', label: 'Initials (fallback)' },
      { key: 'showDivider', target: 'props', type: 'toggle', label: 'Show Divider' },
      { key: 'ctaKey', target: 'props', type: 'i18nText', label: 'CTA Text' },
      { key: 'ctaLink', target: 'props', type: 'url', label: 'CTA Link' },
    ],
  },

  SectionTitle: {
    component: SectionTitleBlock,
    label: 'Section Title',
    category: 'text',
    icon: 'FaFont',
    defaultProps: { textKey: '', align: 'center', level: 'h2' },
    defaultStyle: {},
    schema: [
      { key: 'textKey', target: 'props', type: 'i18nText', label: 'Title Text' },
      { key: 'level', target: 'props', type: 'select', label: 'Level', options: [
        { value: 'h1', label: 'H1' },
        { value: 'h2', label: 'H2' },
        { value: 'h3', label: 'H3' },
      ]},
      { key: 'align', target: 'props', type: 'select', label: 'Alignment', options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]},
    ],
  },

  TextBlock: {
    component: TextBlock,
    label: 'Text Block',
    category: 'text',
    icon: 'FaFont',
    defaultProps: { contentKey: '', align: 'left' },
    defaultStyle: {},
    schema: [
      { key: 'contentKey', target: 'props', type: 'i18nText', label: 'Content', multiline: true },
      { key: 'align', target: 'props', type: 'select', label: 'Alignment', options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]},
    ],
  },

  Header: {
    component: Header,
    label: 'Header',
    category: 'text',
    icon: 'FaFont',
    defaultProps: { textKey: '', level: 'h2', align: 'left' },
    defaultStyle: {},
    schema: [
      { key: 'textKey', target: 'props', type: 'i18nText', label: 'Text' },
      { key: 'level', target: 'props', type: 'select', label: 'Level', options: [
        { value: 'h1', label: 'H1' },
        { value: 'h2', label: 'H2' },
        { value: 'h3', label: 'H3' },
      ]},
      { key: 'align', target: 'props', type: 'select', label: 'Alignment', options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]},
    ],
  },

  CTAButton: {
    component: CTAButton,
    label: 'CTA Button',
    category: 'interactive',
    icon: 'FaArrowRight',
    defaultProps: { labelKey: '', href: '', variant: 'primary', showArrow: true, icon: '' },
    defaultStyle: {},
    schema: [
      { key: 'labelKey', target: 'props', type: 'i18nText', label: 'Button Text' },
      { key: 'href', target: 'props', type: 'url', label: 'Link URL' },
      { key: 'variant', target: 'props', type: 'select', label: 'Style', options: [
        { value: 'primary', label: 'Primary (Filled)' },
        { value: 'outline', label: 'Outline' },
      ]},
      { key: 'showArrow', target: 'props', type: 'toggle', label: 'Show Arrow' },
      { key: 'icon', target: 'props', type: 'icon', label: 'Icon (optional)' },
    ],
  },

  PortfolioItem: {
    component: PortfolioItem,
    label: 'Portfolio Item',
    category: 'content',
    icon: 'FaBriefcase',
    defaultProps: {
      titleKey: '', period: '', subtitleKey: '', descriptionKey: '',
      images: [], tags: [], links: [], reverse: false, demoSlug: '',
    },
    defaultStyle: {},
    schema: [
      { key: 'titleKey', target: 'props', type: 'i18nText', label: 'Title' },
      { key: 'subtitleKey', target: 'props', type: 'i18nText', label: 'Subtitle' },
      { key: 'descriptionKey', target: 'props', type: 'i18nText', label: 'Description', multiline: true },
      { key: 'period', target: 'props', type: 'text', label: 'Period' },
      { key: 'reverse', target: 'props', type: 'toggle', label: 'Reverse Layout' },
      { key: 'demoSlug', target: 'props', type: 'select', label: 'Live demo', options: [
        { value: '', label: 'None' },
        ...Object.keys(demoRegistry).map((slug) => ({ value: slug, label: slug })),
      ]},
      { key: 'tags', target: 'props', type: 'tags', label: 'Tags' },
      { key: 'images', target: 'props', type: 'collection', label: 'Images',
        defaultItem: { url: '', alt: '' },
        collectionSchema: [
          { key: 'url', type: 'image', label: 'Image' },
          { key: 'alt', type: 'text', label: 'Alt Text' },
        ],
      },
      { key: 'links', target: 'props', type: 'collection', label: 'Links',
        defaultItem: { url: '', label: '' },
        collectionSchema: [
          { key: 'url', type: 'url', label: 'URL' },
          { key: 'label', type: 'text', label: 'Label' },
        ],
      },
    ],
  },

  Timeline: {
    component: Timeline,
    label: 'Timeline',
    category: 'content',
    icon: 'FaBriefcase',
    defaultProps: { items: [] },
    defaultStyle: {},
    schema: [
      { key: 'items', target: 'props', type: 'collection', label: 'Timeline Items',
        defaultItem: { id: '', roleKey: '', companyKey: '', period: '', descriptionKey: '' },
        collectionSchema: [
          { key: 'roleKey', type: 'i18nText', label: 'Role' },
          { key: 'companyKey', type: 'i18nText', label: 'Company' },
          { key: 'period', type: 'text', label: 'Period' },
          { key: 'descriptionKey', type: 'i18nText', label: 'Description', multiline: true },
        ],
      },
    ],
  },

  CertificateGrid: {
    component: CertificateGrid,
    label: 'Certificate Grid',
    category: 'content',
    icon: 'FaCertificate',
    defaultProps: { items: [] },
    defaultStyle: {},
    schema: [
      { key: 'items', target: 'props', type: 'collection', label: 'Certificates',
        defaultItem: { id: '', titleKey: '', issuerKey: '', date: '', link: '' },
        collectionSchema: [
          { key: 'titleKey', type: 'i18nText', label: 'Title' },
          { key: 'issuerKey', type: 'i18nText', label: 'Issuer' },
          { key: 'date', type: 'text', label: 'Date' },
          { key: 'link', type: 'url', label: 'Certificate Link' },
        ],
      },
    ],
  },

  FeatureCards: {
    component: FeatureCards,
    label: 'Feature Cards',
    category: 'content',
    icon: 'FaRobot',
    defaultProps: { titleKey: '', descKey: '', features: [] },
    defaultStyle: {},
    schema: [
      { key: 'titleKey', target: 'props', type: 'i18nText', label: 'Section Title' },
      { key: 'descKey', target: 'props', type: 'i18nText', label: 'Description', multiline: true },
      { key: 'features', target: 'props', type: 'collection', label: 'Features',
        defaultItem: { icon: '', titleKey: '', descKey: '' },
        collectionSchema: [
          { key: 'icon', type: 'icon', label: 'Icon' },
          { key: 'titleKey', type: 'i18nText', label: 'Title' },
          { key: 'descKey', type: 'i18nText', label: 'Description' },
        ],
      },
    ],
  },

  TechStack: {
    component: TechStack,
    label: 'Tech Stack',
    category: 'layout',
    icon: 'FaReact',
    defaultProps: { labelKey: '', items: [] },
    defaultStyle: {},
    schema: [
      { key: 'labelKey', target: 'props', type: 'i18nText', label: 'Label' },
      { key: 'items', target: 'props', type: 'collection', label: 'Tech Items',
        defaultItem: { icon: '', label: '' },
        collectionSchema: [
          { key: 'icon', type: 'icon', label: 'Icon' },
          { key: 'label', type: 'text', label: 'Name' },
        ],
      },
    ],
  },

  ContactLinks: {
    component: ContactLinks,
    label: 'Contact Links',
    category: 'interactive',
    icon: 'FaEnvelope',
    defaultProps: { items: [], showDownload: true, downloadKey: '', subtitleKey: '', cvFile: '/documents/cv.pdf' },
    defaultStyle: {},
    schema: [
      { key: 'subtitleKey', target: 'props', type: 'i18nText', label: 'Subtitle' },
      { key: 'showDownload', target: 'props', type: 'toggle', label: 'Show Download Button' },
      { key: 'downloadKey', target: 'props', type: 'i18nText', label: 'Download Text' },
      { key: 'cvFile', target: 'props', type: 'text', label: 'CV File Path (e.g. /documents/cv.pdf)' },
      { key: 'items', target: 'props', type: 'collection', label: 'Contact Items',
        defaultItem: { icon: '', labelKey: '', url: '' },
        collectionSchema: [
          { key: 'icon', type: 'icon', label: 'Icon' },
          { key: 'labelKey', type: 'i18nText', label: 'Label' },
          { key: 'url', type: 'url', label: 'URL' },
        ],
      },
    ],
  },

  ImageBlock: {
    component: ImageBlock,
    label: 'Image',
    category: 'media',
    icon: 'FaImage',
    defaultProps: { src: '', alt: '', captionKey: '', align: 'center', maxWidth: '100%' },
    defaultStyle: {},
    schema: [
      { key: 'src', target: 'props', type: 'image', label: 'Image' },
      { key: 'alt', target: 'props', type: 'text', label: 'Alt Text' },
      { key: 'captionKey', target: 'props', type: 'i18nText', label: 'Caption' },
      { key: 'align', target: 'props', type: 'select', label: 'Alignment', options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]},
      { key: 'maxWidth', target: 'props', type: 'text', label: 'Max Width' },
    ],
  },

  Carousel: {
    component: Carousel,
    label: 'Carousel',
    category: 'media',
    icon: 'FaImage',
    defaultProps: { images: [], alt: '' },
    defaultStyle: {},
    schema: [
      { key: 'alt', target: 'props', type: 'text', label: 'Alt Text' },
      { key: 'images', target: 'props', type: 'collection', label: 'Images',
        defaultItem: { url: '', alt: '' },
        collectionSchema: [
          { key: 'url', type: 'image', label: 'Image' },
          { key: 'alt', type: 'text', label: 'Alt Text' },
        ],
      },
    ],
  },

  Separator: {
    component: Separator,
    label: 'Separator',
    category: 'layout',
    icon: 'FaGripVertical',
    defaultProps: { width: '60px', height: '2px', align: 'center' },
    defaultStyle: { marginTop: '1.5rem', marginBottom: '1.5rem' },
    schema: [
      { key: 'width', target: 'props', type: 'text', label: 'Width' },
      { key: 'height', target: 'props', type: 'text', label: 'Height' },
      { key: 'align', target: 'props', type: 'select', label: 'Alignment', options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ]},
    ],
  },

  PomodoroTimer: {
    component: PomodoroTimerBlock,
    label: 'Pomodoro Timer',
    category: 'interactive',
    icon: 'FaPlay',
    defaultProps: { titleKey: '', descKey: '' },
    defaultStyle: {},
    schema: [
      { key: 'titleKey', target: 'props', type: 'i18nText', label: 'Title' },
      { key: 'descKey', target: 'props', type: 'i18nText', label: 'Description', multiline: true },
    ],
  },

  EditModeToggle: {
    component: EditModeToggleBlock,
    label: 'Edit Mode Toggle',
    category: 'interactive',
    icon: 'FaEdit',
    defaultProps: {},
    defaultStyle: {},
    schema: [],
  },
};

export function getBlock(type) {
  return blockRegistry[type] || null;
}

export function getBlockList() {
  return Object.entries(blockRegistry).map(([type, def]) => ({
    type,
    label: def.label,
    category: def.category,
    icon: def.icon,
  }));
}

export function createBlock(type) {
  const def = blockRegistry[type];
  if (!def) return null;
  return {
    id: `block_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    type,
    style: { ...def.defaultStyle },
    props: JSON.parse(JSON.stringify(def.defaultProps)),
  };
}

export default blockRegistry;
